import React, { forwardRef, type CSSProperties, useState } from "react";
import { useDataTableContext } from "@/components/DataTable/Context/DataTableContext";
import { TableBody, TableRow, TableCell } from "@/components/ui/Table";
import {
  getColumnWidthStyle,
  getCellWidthStyle,
} from "../utils/DataTableUtils";
import { Skeleton } from "@/components/ui/Skeleton";
import { flexRender, Row, type Cell } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";

const getCommonPinningStyles = (
  cell: Cell<unknown, unknown>,
  rowIndex: number,
  isHovered: boolean,
): CSSProperties => {
  const { column } = cell;
  const isPinned = column.getIsPinned();

  if (!isPinned) {
    return {
      position: "relative",
      width: column.getSize(),
      zIndex: 0,
    };
  }

  const isEvenRow = rowIndex % 2 === 0;
  let backgroundColor;

  if (isHovered) {
    backgroundColor = "var(--background-light)";
  } else {
    backgroundColor = isEvenRow ? "var(--table-accent)" : "var(--background)";
  }

  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: "sticky",
    width: column.getSize(),
    zIndex: 1,
    backgroundColor,
    transition: "none",
  };
};

interface BodyCellsProps<TData> {
  cellEvenBgColor?: string;
  onRowClick?: (row: Row<TData>) => void;
}

export const BodyCellsComponent = forwardRef<
  HTMLTableSectionElement,
  BodyCellsProps<unknown>
>(function BodyCellsComponent<TData>(
  { cellEvenBgColor = "bg-table-accent", onRowClick }: BodyCellsProps<TData>,
  ref: React.Ref<HTMLTableSectionElement>,
) {
  const {
    columns,
    table,
    isLoading,
    numSkeletonRows = 5,
    renderedDimensions = { contentDimensions: {} },
  } = useDataTableContext();
  const { t } = useTranslation();
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  if (!columns || !Array.isArray(columns)) {
    return (
      <TableBody ref={ref} role="rowgroup">
        <TableRow role="row">
          <TableCell className="h-24 text-center" role="cell">
            {t("global.noResults")}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const lastRowIndex = table?.getCoreRowModel().rows.length
    ? table?.getCoreRowModel().rows.length - 1
    : 0;

  return (
    <TableBody ref={ref} role="rowgroup">
      {isLoading ? (
        Array.from({ length: numSkeletonRows }).map((_, index) => (
          <TableRow
            key={`skeleton-row-${index}`}
            role="row"
            aria-label={t("dataTable.loadingRow")}
          >
            {columns.map((column, colIndex) => {
              const columnId = column.id;
              const contentDimension = columnId
                ? renderedDimensions.contentDimensions[columnId]
                : undefined;

              const totalCellHeight = contentDimension?.height || 48;

              const tableColumn = table
                ?.getAllColumns()
                .find((col) => col.id === columnId);

              return (
                <TableCell
                  key={`skeleton-cell-${index}-${colIndex}`}
                  role="cell"
                  aria-colindex={colIndex + 1}
                  style={
                    tableColumn ? getColumnWidthStyle(tableColumn) : undefined
                  }
                >
                  <div
                    className="flex items-center p-4 pl-4 text-left text-sm text-foreground"
                    style={{
                      height: totalCellHeight,
                    }}
                  >
                    <Skeleton
                      className="h-6"
                      style={
                        contentDimension
                          ? {
                              width: contentDimension.width,
                            }
                          : {
                              width: "100%",
                            }
                      }
                    />
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        ))
      ) : table?.getCoreRowModel().rows?.length ? (
        table?.getCoreRowModel().rows.map((row, index) => (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && "selected"}
            className={clsx(
              onRowClick ? "cursor-pointer" : "",
              index === lastRowIndex && "!border-b !border-border",
              index % 2 === 0 ? cellEvenBgColor : "",
            )}
            style={{
              backgroundColor:
                hoveredRowIndex === index
                  ? "var(--background-light)"
                  : undefined,
              transition: "none",
            }}
            role="row"
            aria-selected={row.getIsSelected()}
            aria-rowindex={index + 1}
            onMouseEnter={() => setHoveredRowIndex(index)}
            onMouseLeave={() => setHoveredRowIndex(null)}
            onClick={() => onRowClick?.(row as Row<TData>)}
          >
            {row.getVisibleCells().map((cell, cellIndex) => {
              const isHovered = hoveredRowIndex === index;
              const pinningStyles = getCommonPinningStyles(
                cell,
                index,
                isHovered,
              );
              const widthStyles = getCellWidthStyle(cell);

              return (
                <TableCell
                  key={`${cell.id}-${cellIndex}`}
                  role="cell"
                  aria-colindex={cell.column.getIndex() + 1}
                  style={{
                    ...widthStyles,
                    ...pinningStyles,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </TableRow>
        ))
      ) : (
        <TableRow role="row">
          <TableCell
            colSpan={columns.length}
            className="h-24 text-center"
            role="cell"
            aria-label={t("dataTable.noResultsFound")}
          >
            {t("global.noResults")}
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
});
